import React, { useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  Bot, Clock, Video, Globe, CheckCircle2, Loader2, ChevronLeft, ChevronRight,
  AlertTriangle, CalendarDays,
} from "lucide-react";
import Header from "../Header&Footer/Header";
import DemoBanner from "./features/DemoBanner";
import { getApiUrlsWithFallback } from "@/config/api";

// 30-min slots, 10:00–18:00 IST (24h internal; displayed per the 12h/24h toggle).
const SLOTS = [
  "10:00", "10:30", "11:00", "11:30", "12:00", "12:30", "13:00", "13:30",
  "14:00", "14:30", "15:00", "15:30", "16:00", "16:30", "17:00", "17:30", "18:00",
];
const WD = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"];
const WD_LONG = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["January", "February", "March", "April", "May", "June", "July",
  "August", "September", "October", "November", "December"];

const pad = (n) => String(n).padStart(2, "0");
const to12h = (hhmm) => {
  const [h, m] = hhmm.split(":").map(Number);
  const ap = h >= 12 ? "pm" : "am";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${pad(m)}${ap}`;
};
const sameDay = (a, b) => a && b && a.y === b.y && a.m === b.m && a.d === b.d;
const isValidEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((e || "").trim());

const inputCls =
  "w-full rounded-xl bg-secondary/40 border border-border px-4 py-2.5 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/30";

function Field({ label, required, hint, children }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-muted-foreground mb-1.5">
        {label}
        {required && <span className="text-destructive"> *</span>}
        {hint && <span className="font-normal text-muted-foreground/70"> {hint}</span>}
      </label>
      {children}
    </div>
  );
}

export default function BookDemo() {
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const now = new Date();
  const todayMid = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  // optional scheduling
  const [cursor, setCursor] = useState({ y: now.getFullYear(), m: now.getMonth() });
  const [selDate, setSelDate] = useState(null); // {y,m,d}
  const [selTime, setSelTime] = useState("");   // "HH:MM"
  const [hour12, setHour12] = useState(true);
  const [wantTime, setWantTime] = useState(false);

  // lead fields (prefill from any personalized-link params)
  const [name, setName] = useState(params.get("name") || "");
  const [title, setTitle] = useState(params.get("title") || params.get("role") || "");
  const [email, setEmail] = useState(params.get("email") || "");
  const [company, setCompany] = useState(params.get("company") || "");
  const [phone, setPhone] = useState(params.get("phone") || "");
  const [comments, setComments] = useState("");
  const intent = (params.get("intent") || "").toLowerCase();

  const [phase, setPhase] = useState("form"); // form | booking | done
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const grid = useMemo(() => {
    const { y, m } = cursor;
    const firstDow = (new Date(y, m, 1).getDay() + 6) % 7; // Monday = 0
    const days = new Date(y, m + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < firstDow; i++) cells.push(null);
    for (let d = 1; d <= days; d++) cells.push(d);
    return cells;
  }, [cursor]);

  const dayEnabled = (d) => {
    if (!d) return false;
    const dt = new Date(cursor.y, cursor.m, d);
    if (dt < todayMid) return false;      // past
    if (dt.getDay() === 0) return false;  // Sundays off
    return true;
  };

  const canPrevMonth =
    cursor.y > now.getFullYear() || (cursor.y === now.getFullYear() && cursor.m > now.getMonth());

  const shiftMonth = (delta) => setCursor((c) => {
    let m = c.m + delta, y = c.y;
    if (m < 0) { m = 11; y -= 1; }
    if (m > 11) { m = 0; y += 1; }
    return { y, m };
  });

  const pickDay = (d) => {
    if (!dayEnabled(d)) return;
    setSelDate({ y: cursor.y, m: cursor.m, d });
    setSelTime("");
  };

  const selDow = selDate ? new Date(selDate.y, selDate.m, selDate.d).getDay() : 0;
  const rightHeader = selDate ? `${WD_LONG[selDow]} ${pad(selDate.d)}` : "";

  const submit = async () => {
    if (!name.trim() || !title.trim() || !isValidEmail(email) || !company.trim() || !phone.trim()) {
      setError("Please fill in your name, job title, a valid work email, company and phone.");
      return;
    }
    if (wantTime && (!selDate || !selTime)) {
      setError("Pick a date and time, or untick “pick a specific time”.");
      return;
    }
    setError(""); setPhase("booking");
    const start_iso = (wantTime && selDate && selTime)
      ? `${selDate.y}-${pad(selDate.m + 1)}-${pad(selDate.d)}T${selTime}:00` : "";
    const payload = {
      name: name.trim(), title: title.trim(), email: email.trim().toLowerCase(),
      company: company.trim(), phone: phone.trim(), comments: comments.trim(),
      start_iso, intent,
    };

    let data = null;
    for (const url of getApiUrlsWithFallback("/api/demo/zia/book")) {
      try {
        const res = await fetch(url, {
          method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload),
        });
        const j = await res.json().catch(() => null);
        if (j) { data = j; break; }
      } catch (e) { /* try next */ }
    }
    if (data && data.ok) { setResult(data); setPhase("done"); }
    else { setError("Something went wrong submitting your request. Please try again."); setPhase("form"); }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />
      <main className="px-4 sm:px-8 pt-24 pb-14">
        {phase !== "done" && (
          <div className="max-w-6xl mx-auto mb-10">
            <DemoBanner onStart={() => navigate("/hirewithus/demo?talk=1")} showCta={false} showTiming={false} />
          </div>
        )}

        {phase === "done" ? (
          <div className="max-w-lg mx-auto rounded-2xl border border-border bg-card shadow-sm p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-success/15 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 size={34} className="text-success" />
            </div>
            {result?.booked ? (
              <>
                <h2 className="text-2xl font-bold mb-1">You're booked!</h2>
                <p className="text-muted-foreground mb-1">{result?.slot_label}</p>
                <p className="text-muted-foreground text-sm mb-6">
                  A Google Calendar invite is on its way to {email}.
                </p>
                {result?.meet_link && (
                  <a href={result.meet_link} target="_blank" rel="noreferrer"
                     className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-6 py-3 font-semibold hover:bg-primary/90 transition">
                    <Video size={18} /> Join on Google Meet
                  </a>
                )}
              </>
            ) : (
              <>
                <h2 className="text-2xl font-bold mb-1">Thank you!</h2>
                <p className="text-muted-foreground mb-1">Your demo request is in.</p>
                <p className="text-muted-foreground text-sm">
                  Our team will reach out shortly to schedule your walkthrough. A confirmation is on
                  its way to {email}.
                </p>
              </>
            )}
          </div>
        ) : (
          // ─────────────── Form-first request card ───────────────
          <div className="max-w-2xl mx-auto rounded-2xl border border-border bg-card shadow-sm overflow-hidden">
            {/* event header */}
            <div className="flex items-start gap-4 p-6 border-b border-border">
              <div className="w-11 h-11 rounded-full bg-primary flex items-center justify-center shadow shrink-0">
                <Bot size={22} className="text-primary-foreground" />
              </div>
              <div>
                <div className="text-sm text-muted-foreground">SearchJob.ai</div>
                <h1 className="text-xl font-bold mt-0.5 leading-snug">Book a demo with our team</h1>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1.5"><Clock size={14} /> 30 min</span>
                  <span className="flex items-center gap-1.5"><Video size={14} className="text-primary" /> Google Meet</span>
                  <span className="flex items-center gap-1.5"><Globe size={14} /> Asia/Kolkata (IST)</span>
                </div>
              </div>
            </div>

            {/* form */}
            <div className="p-6">
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Full name" required>
                  <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Priya Nair" className={inputCls} />
                </Field>
                <Field label="Job title" required>
                  <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Head of Talent" className={inputCls} />
                </Field>
                <Field label="Work email" required>
                  <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="you@company.com" className={inputCls} />
                </Field>
                <Field label="Company" required>
                  <input value={company} onChange={(e) => setCompany(e.target.value)} placeholder="e.g. Acme Corp" className={inputCls} />
                </Field>
                <div className="sm:col-span-2">
                  <Field label="Phone" required>
                    <input value={phone} onChange={(e) => setPhone(e.target.value)} type="tel" placeholder="+91 98765 43210" className={inputCls} />
                  </Field>
                </div>
                <div className="sm:col-span-2">
                  <Field label="Comments" hint="(optional)">
                    <textarea value={comments} onChange={(e) => setComments(e.target.value)} rows={3}
                      placeholder="What roles are you hiring for, or anything you'd like us to prepare?"
                      className={`${inputCls} resize-none`} />
                  </Field>
                </div>
              </div>

              {/* optional: pick a time now */}
              <label className="flex items-center gap-2.5 mt-5 cursor-pointer select-none">
                <input type="checkbox" checked={wantTime} onChange={(e) => setWantTime(e.target.checked)}
                  className="w-4 h-4 accent-primary" />
                <span className="text-sm font-medium text-foreground">
                  I'd like to pick a specific time now
                  <span className="text-muted-foreground font-normal"> (optional)</span>
                </span>
              </label>

              {wantTime && (
                <div className="mt-4 rounded-xl border border-border p-4 grid md:grid-cols-2 gap-5">
                  {/* month calendar */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <div className="text-sm font-semibold">
                        {MONTHS[cursor.m]} <span className="text-muted-foreground font-normal">{cursor.y}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <button onClick={() => shiftMonth(-1)} disabled={!canPrevMonth}
                          className="w-7 h-7 rounded-lg flex items-center justify-center text-muted-foreground hover:bg-accent disabled:opacity-30 disabled:hover:bg-transparent transition">
                          <ChevronLeft size={16} />
                        </button>
                        <button onClick={() => shiftMonth(1)}
                          className="w-7 h-7 rounded-lg flex items-center justify-center text-muted-foreground hover:bg-accent transition">
                          <ChevronRight size={16} />
                        </button>
                      </div>
                    </div>
                    <div className="grid grid-cols-7 gap-1 text-center text-[10px] font-semibold text-muted-foreground mb-1">
                      {WD.map((w) => <div key={w} className="py-1">{w}</div>)}
                    </div>
                    <div className="grid grid-cols-7 gap-1">
                      {grid.map((d, i) => {
                        if (!d) return <div key={i} />;
                        const isSel = sameDay(selDate, { y: cursor.y, m: cursor.m, d });
                        const enabled = dayEnabled(d);
                        return (
                          <button key={i} onClick={() => pickDay(d)} disabled={!enabled}
                            className={`aspect-square rounded-lg text-xs font-medium transition ${
                              isSel ? "bg-foreground text-background"
                                : enabled ? "bg-secondary/60 text-foreground hover:bg-primary/15 hover:text-primary"
                                : "text-muted-foreground/40 cursor-default"}`}>
                            {d}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* time slots */}
                  <div>
                    {!selDate ? (
                      <div className="h-full flex flex-col items-center justify-center text-center text-xs text-muted-foreground py-8">
                        <CalendarDays size={20} className="mb-2 text-muted-foreground/60" />
                        Pick a day to see times.
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center justify-between mb-2">
                          <div className="text-sm font-semibold">{rightHeader}</div>
                          <div className="inline-flex rounded-lg border border-border p-0.5 text-[11px]">
                            <button onClick={() => setHour12(true)}
                              className={`px-2 py-0.5 rounded-md transition ${hour12 ? "bg-secondary font-semibold text-foreground" : "text-muted-foreground"}`}>12h</button>
                            <button onClick={() => setHour12(false)}
                              className={`px-2 py-0.5 rounded-md transition ${!hour12 ? "bg-secondary font-semibold text-foreground" : "text-muted-foreground"}`}>24h</button>
                          </div>
                        </div>
                        <div className="space-y-1.5 max-h-[220px] overflow-y-auto pr-1">
                          {SLOTS.map((s) => (
                            <button key={s} onClick={() => setSelTime(s)}
                              className={`w-full rounded-lg border py-2 text-sm font-semibold transition ${
                                selTime === s ? "border-primary bg-primary/10 text-primary"
                                  : "border-border text-foreground hover:border-primary hover:text-primary hover:bg-primary/5"}`}>
                              {hour12 ? to12h(s) : s}
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}

              {error && (
                <div className="flex items-start gap-2 text-sm text-destructive mt-4">
                  <AlertTriangle size={15} className="shrink-0 mt-0.5" /> <span>{error}</span>
                </div>
              )}

              <button onClick={submit} disabled={phase === "booking"}
                className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full bg-primary text-primary-foreground px-6 py-3 font-semibold shadow-lg hover:bg-primary/90 transition disabled:opacity-60">
                {phase === "booking"
                  ? (<><Loader2 size={18} className="animate-spin" /> Submitting…</>)
                  : (<>Request demo</>)}
              </button>
              <p className="text-[11px] text-muted-foreground mt-3 text-center">
                {wantTime
                  ? "You'll get a Google Calendar invite with a Meet link at the email above."
                  : "Our team will email you to confirm a time — or tick the box above to pick one now."}
              </p>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
